import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Card } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import api from '@/utils/api';

const TeamPage = () => {
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Main team members with images
  const mainMembers = [
    {
      name: "Vijay Sethupathi",
      designation: "Chairman",
      role: "Actor / Producer / Writer / Entrepreneur / Philanthropist",
      image: "https://customer-assets.emergentagent.com/job_76316ef1-f3dc-4311-a1ba-a9b7bcab55b0/artifacts/sluz5oae_Vijay%20Sethupathi.jpg",
      order: 1
    },
    {
      name: "I.B.Veerarahavan",
      designation: "Founder & Managing Trustee",
      role: "",
      image: "https://customer-assets.emergentagent.com/job_76316ef1-f3dc-4311-a1ba-a9b7bcab55b0/artifacts/q41i3ioo_ib-veeragavan.jpg",
      order: 2
    }
  ];

  // Other trustees without images (displayed together)
  const otherTrustees = {
    treasurer: "V.Sangeetha",
    trustees: ["S.Chandra Sekaran", "R.Anandaraj", "A.Suresh"]
  };

  useEffect(() => {
    fetchPageContent();
  }, []);

  const fetchPageContent = async () => {
    try {
      const response = await api.get('/cms/page/team');
      if (response.data && response.data.content) {
        setPageContent(response.data);
      }
    } catch (error) {
      console.log('Using default team content');
    } finally {
      setLoading(false);
    }
  };

  const pageTitle = pageContent?.content?.title || "Our Trustee Team";
  const pageSubtitle = pageContent?.content?.subtitle || "We Have Trustee Team for VVVSI-Trust";
  const pageDescription = pageContent?.content?.description || "Chairman, Founder, Managing Trustee, Treasurer & Trustees";

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse space-y-8">
            <div className="h-10 bg-slate-200 rounded w-1/3 mx-auto"></div>
            <div className="h-6 bg-slate-200 rounded w-1/2 mx-auto"></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {[1,2].map(i => (
                <div key={i} className="h-80 bg-slate-200 rounded-xl"></div>
              ))}
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Helmet>
        <title>Our Trustee Team - VVVSI Trust</title>
        <meta name="description" content="Meet the leadership team of VVVSI Trust - Chairman, Founder, Managing Trustee, Treasurer & Trustees" />
      </Helmet>
      
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Outfit'}}>
            {pageTitle}
          </h1>
          <p className="text-xl text-teal-600 font-medium mb-2">
            {pageSubtitle}
          </p>
          <p className="text-slate-600">
            {pageDescription}
          </p>
        </div>

        {/* Main Team Members - Chairman and Founder */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
          {mainMembers.map((member, index) => (
            <Card 
              key={index}
              className="group p-8 bg-white border-slate-200 hover:shadow-xl transition-all duration-300 text-center"
            >
              {/* Member Photo */}
              <div className="mb-6 flex justify-center">
                <div className="w-40 h-40 rounded-full overflow-hidden border-4 border-teal-100 shadow-lg group-hover:border-teal-200 transition-colors">
                  <img 
                    src={member.image} 
                    alt={member.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Member Info */}
              <h3 className="text-2xl font-semibold text-slate-900 mb-2" style={{fontFamily: 'Outfit'}}>
                {member.name}
              </h3>
              <p className="text-teal-600 font-medium text-lg mb-2">
                {member.designation}
              </p>
              {member.role && (
                <p className="text-sm text-slate-600 leading-relaxed">
                  {member.role}
                </p>
              )}
            </Card>
          ))}
        </div>

        {/* Other Trustees Card - Treasurer and Trustees Together */}
        <div className="max-w-3xl mx-auto mb-12">
          <Card className="p-8 bg-white border-slate-200 text-center">
            <h3 className="text-xl font-semibold text-slate-900 mb-6" style={{fontFamily: 'Outfit'}}>
              Treasurer & Trustees
            </h3>
            
            {/* Treasurer */}
            <div className="mb-6 pb-6 border-b border-slate-100">
              <p className="text-teal-600 font-medium text-sm mb-1">Treasurer</p>
              <p className="text-xl font-semibold text-slate-900">{otherTrustees.treasurer}</p>
            </div>
            
            {/* Trustees */}
            <div>
              <p className="text-teal-600 font-medium text-sm mb-3">Trustees</p>
              <div className="flex flex-wrap justify-center gap-4">
                {otherTrustees.trustees.map((name, index) => (
                  <span 
                    key={index}
                    className="text-lg font-semibold text-slate-900 px-4 py-2 bg-slate-50 rounded-lg"
                  >
                    {name}
                  </span>
                ))}
              </div>
            </div>
          </Card>
        </div>

        {/* Mission Statement */}
        <Card className="p-8 bg-gradient-to-r from-teal-50 to-emerald-50 border-teal-200 text-center max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{fontFamily: 'Outfit'}}>
            Our Mission
          </h2>
          <p className="text-slate-700 leading-relaxed">
            VVVSI Trust is committed to serving society through honest and transparent employment support. 
            Our team works tirelessly to connect job seekers directly with genuine employers, 
            bringing dignity, security, and hope to every family we serve.
          </p>
        </Card>
      </main>

      <Footer />
    </div>
  );
};

export default TeamPage;
